/**
 * Helper functions for coordinate system mapping
 * Between GameCore (2D) and THREE.js (3D)
 * 
 * Game.js uses THREE.js coordinate system:
 * - X axis: Left (-) to Right (+)
 * - Y axis: Down (-) to Up (+) [NOT USED for gameplay, always 0]
 * - Z axis: Back (-) to Front (+)
 * 
 * Gameplay happens in X-Z plane (horizontal).
 * 
 * GameCore uses 2D coordinate system:
 * - X axis: Left to Right (same as THREE.js X)
 * - Y axis: Top to Bottom (maps to THREE.js Z)
 */

import * as THREE from 'three';

export class CoordinateMapper {
    /**
     * Convert GameCore 2D position to THREE.js 3D position
     * @param {Object} gameCorePos - {x, y}
     * @returns {Object} - {x, y, z} where y is always 0
     */
    static toThreeJS(gameCorePos) {
        return {
            x: gameCorePos.x,
            y: 0,  // Always 0 for 2D gameplay
            z: gameCorePos.y  // GameCore Y → THREE.js Z
        };
    }
    
    /**
     * Convert THREE.js 3D position to GameCore 2D position
     * @param {THREE.Vector3|Object} threeJSPos - {x, y, z} or Vector3
     * @returns {Object} - {x, y}
     */
    static toGameCore(threeJSPos) {
        const x = threeJSPos.x || threeJSPos.getX?.() || 0;
        const z = threeJSPos.z || threeJSPos.getZ?.() || 0;
        
        return {
            x: x,
            y: z  // THREE.js Z → GameCore Y
        };
    }
    
    /**
     * Convert GameCore 2D velocity to THREE.js 3D velocity
     * @param {Object} gameCoreVel - {x, y}
     * @returns {Object} - {x, y, z} where y is always 0
     */
    static velToThreeJS(gameCoreVel) {
        return {
            x: gameCoreVel.x,
            y: 0,
            z: gameCoreVel.y  // GameCore Y → THREE.js Z
        };
    }
    
    /**
     * Convert THREE.js 3D velocity to GameCore 2D velocity
     * @param {THREE.Vector3|Object} threeJSVel - {x, y, z} or Vector3
     * @returns {Object} - {x, y}
     */
    static velToGameCore(threeJSVel) {
        const x = threeJSVel.x || threeJSVel.getX?.() || 0;
        const z = threeJSVel.z || threeJSVel.getZ?.() || 0;
        
        return {
            x: x,
            y: z  // THREE.js Z → GameCore Y
        };
    }
    
    /**
     * Calculate world boundaries from camera FOV
     * This matches the calculation in Game.js lines 65-69
     * @param {THREE.Camera} camera - THREE.js camera
     * @returns {Object} - {width, height, maxX, maxY}
     */
    static calculateWorldBounds(camera) {
        const vFOV = (camera.fov * Math.PI) / 180;
        const height = 2 * Math.tan(vFOV / 2) * Math.abs(camera.position.y);
        const width = height * camera.aspect;
        
        return {
            width,
            height,
            maxX: width / 2,
            maxY: height / 2
        };
    }
}



